import { FC, useRef, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import useRoiPatientAge, {
  AgeDistributionDetail,
} from 'hooks/useRoiPatientAge';
import { COLOR_CODES } from 'utils/constants';

ChartJS.register(ArcElement, Tooltip, Legend);

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  label: string;
  value: number;
  pct: string;
  color: string;
}

interface AgeOfPatientsProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  clinicIds?: string[];
}

export const formatRangeLabel = (d: AgeDistributionDetail) => {
  if (d.toAge >= 100) return `>${d.fromAge}`;
  if (d.fromAge <= 1) return `<${d.toAge}`;
  return `${d.fromAge}-${d.toAge}`;
};

const AgeOfPatients: FC<AgeOfPatientsProps> = ({
  selectedDateRange,
  hourType,
  clinicIds,
}) => {
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    label: '',
    value: 0,
    pct: '',
    color: '',
  });
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isValidating } = useRoiPatientAge({
    startDate: selectedDateRange.fromDate,
    endDate: selectedDateRange.toDate,
    hourType,
    ageRanges: ['18-29', '30-39', '40-49', '50-59', '60-69', '0-18', '69-100'],
    clinicIds,
  });

  const distribution = [...(data?.periodData?.ageDistribution ?? [])]
    .filter((d) => d.totalPatients > 0)
    .sort((a, b) => a.fromAge - b.fromAge);

  const isEmpty = distribution.length === 0;

  const chartData = {
    labels: isEmpty ? ['No data'] : distribution.map(formatRangeLabel),
    datasets: [
      {
        data: isEmpty ? [1] : distribution.map((d) => d.totalPatients),
        backgroundColor: isEmpty
          ? ['#E8E8E8']
          : distribution.map((_, i) => COLOR_CODES[i % COLOR_CODES.length]),
        borderColor: '#FFFFFF',
        borderWidth: isEmpty ? 0 : 1,
        hoverBorderColor: '#FFFFFF',
        hoverBorderWidth: isEmpty ? 0 : 1,
        hoverOffset: 0,
      },
    ],
  };

  return (
    <div className="p-2.4 shadow-elevation-06-hover bg-white flex-1 min-h-[38rem] flex flex-col">
      {isLoading || isValidating ? (
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner className="all-child:fill-magenta w-4 h-4" />
        </div>
      ) : (
        <>
          <span className="text-16 font-bold leading-[2.4rem]">
            Age of Patients
          </span>
          <div ref={chartContainerRef} className="mt-1.6 h-[22rem] relative">
            <Doughnut
              data={chartData}
              options={{
                cutout: '60%',
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    enabled: false,
                    external: isEmpty
                      ? undefined
                      : ({ chart, tooltip: tip }: any) => {
                          if (tip.opacity === 0) {
                            setTooltip((prev) => ({
                              ...prev,
                              visible: false,
                            }));
                            return;
                          }

                          const dataIndex = tip.dataPoints?.[0]?.dataIndex ?? 0;
                          const segment = distribution[dataIndex];
                          if (!segment) return;

                          const value = segment.totalPatients;
                          const total = distribution.reduce(
                            (a, b) => a + b.totalPatients,
                            0
                          );
                          const pct =
                            total > 0
                              ? ((value / total) * 100).toFixed(1)
                              : '0.0';
                          const label = `Age ${formatRangeLabel(segment)}`;
                          const color =
                            COLOR_CODES[dataIndex % COLOR_CODES.length];

                          const containerRect =
                            chartContainerRef.current?.getBoundingClientRect();
                          const canvasRect =
                            chart.canvas.getBoundingClientRect();

                          const x =
                            canvasRect.left -
                            (containerRect?.left ?? 0) +
                            tip.caretX;
                          const y =
                            canvasRect.top -
                            (containerRect?.top ?? 0) +
                            tip.caretY;

                          setTooltip({
                            visible: true,
                            x,
                            y,
                            label,
                            value,
                            pct,
                            color,
                          });
                        },
                  },
                },
              }}
            />

            {tooltip.visible && (
              <div
                className="absolute z-20 pointer-events-none bg-white text-darkest-grey text-14 leading-[2.1rem] px-1.6 py-1.2 rounded-[1rem] shadow-primary whitespace-nowrap"
                style={{
                  left: tooltip.x,
                  top: tooltip.y,
                  transform: 'translate(0, -110%)',
                }}
              >
                <div className="font-bold mb-0.4">{tooltip.label}</div>
                <div className="flex items-center gap-x-0.8">
                  <span
                    className="w-0.8 h-0.8 rounded-full shrink-0"
                    style={{ backgroundColor: tooltip.color }}
                  />
                  <span>{tooltip.value.toLocaleString()} patients</span>
                  <span className="text-mid-grey">({tooltip.pct}%)</span>
                </div>
              </div>
            )}
          </div>

          {!isEmpty && (
            <div className="mt-4 flex flex-wrap justify-center gap-y-1.6 gap-x-2.4 mx-auto">
              {distribution.map((d, i) => (
                <div
                  key={`${d.fromAge}-${d.toAge}`}
                  className="flex items-center gap-x-0.8"
                >
                  <span
                    className="w-0.8 h-0.8 rounded-full shrink-0"
                    style={{
                      backgroundColor: COLOR_CODES[i % COLOR_CODES.length],
                    }}
                  />
                  <span className="text-14 text-darkest-grey">
                    {formatRangeLabel(d)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AgeOfPatients;
